import { useEffect } from "react";

const HighlightManager = ({ editor, isPresentMode }) => {
  useEffect(() => {
    if (!isPresentMode || !editor) return;

    let removeButton = null;
    let selectionFrom = null;
    let selectionTo = null;
    let selectionRemoveFrom = null;
    let selectionRemoveTo = null;

    const removeRemoveButton = () => {
      if (removeButton) {
        removeButton.remove();
        removeButton = null;
      }
    };

    const createRemoveButton = (element, from, to) => {
      removeRemoveButton();

      removeButton = document.createElement("div");
      removeButton.innerText = "❌";
      removeButton.title = "Click to remove highlight of selected text";

      Object.assign(removeButton.style, {
        position: "absolute",
        background: "white",
        color: "red",
        border: "1px solid red",
        borderRadius: "50%",
        fontSize: "12px",
        width: "20px",
        height: "20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        zIndex: 9999,
      });

      const rect = element.getBoundingClientRect();
      removeButton.style.top = `${rect.top - 30 + window.scrollY}px`;
      removeButton.style.left = `${rect.right - 10 + window.scrollX}px`;

      removeButton.onclick = () => {
        editor
          .chain()
          .focus()
          .setTextSelection({ from, to })
          .unsetMark("highlight")
          .setTextSelection(from)
          .run();
        removeRemoveButton();
        selectionRemoveFrom = null;
        selectionRemoveTo = null;
      };

      document.body.appendChild(removeButton);
    };

    const handleSelectionUpdate = () => {
      const { from, to, empty } = editor.state.selection;

      if (!empty) {
        const hasHighlight = editor.state.doc.rangeHasMark(
          from,
          to,
          editor.schema.marks.highlight
        );
        if (hasHighlight) {
          selectionFrom = from;
          selectionTo = to;
          selectionRemoveFrom = from;
          selectionRemoveTo = to;
        } else {
          selectionFrom = from;
          selectionTo = to;
          selectionRemoveFrom = from;
          selectionRemoveTo = to;
          removeRemoveButton();
        }
      } else {
        selectionFrom = null;
        selectionTo = null;
        selectionRemoveFrom = null;
        selectionRemoveTo = null;
        removeRemoveButton();
      }
    };

    const applyHighlightOrShowRemove = () => {
      if (selectionFrom == null || selectionTo == null) return;

      const hasHighlight = editor.state.doc.rangeHasMark(
        selectionFrom,
        selectionTo,
        editor.schema.marks.highlight
      );

      const selection = window.getSelection();
      const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
      const rect = range?.getBoundingClientRect();
      if (!rect) return;

      if (hasHighlight) {
        setTimeout(() => {
          let markElement = null;
          let node = range?.startContainer;

          while (node && node !== document.body) {
            if (node.nodeType === 1 && node.tagName === "MARK") {
              markElement = node;
              break;
            }
            node = node.parentNode;
          }

          if (!markElement) {
            markElement = document.querySelector("mark");
          }

          if (markElement) {
            createRemoveButton(
              markElement,
              selectionRemoveFrom,
              selectionRemoveTo
            );
          }
        }, 50);
      } else {
        editor
          .chain()
          .focus()
          .setTextSelection({ from: selectionFrom, to: selectionTo })
          .setMark("highlight", { color: "yellow" })
          .setTextSelection(selectionTo)
          .run();
      }

      selectionFrom = null;
      selectionTo = null;
    };

    editor.on("selectionUpdate", handleSelectionUpdate);
    document.addEventListener("mouseup", applyHighlightOrShowRemove);
    document.addEventListener("keyup", applyHighlightOrShowRemove);

    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate);
      document.removeEventListener("mouseup", applyHighlightOrShowRemove);
      document.removeEventListener("keyup", applyHighlightOrShowRemove);
      removeRemoveButton();
    };
  }, [isPresentMode, editor]);

  return null; // This component doesn’t render anything
};

export default HighlightManager;
